import {
  faCheck,
  faCircleDashed,
  faCircleNodes,
  faGrid,
  faSpinner,
  faStar,
  faXmark,
  type IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { faStar as faStarSolid } from "@fortawesome/pro-solid-svg-icons";
import { Badge, Button, Icon, Message } from "@intility/bifrost-react";
import type { Columns } from "@intility/gjallarbru";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useClients } from "../../api/clients";
import { usePlatforms } from "../../api/platforms";
import type { ClientListItem, PlatformListItem } from "../../api/types";
import { auth } from "../../auth";
import { RoleBadge } from "../../components/RoleBadge";
import { PlatformsTable } from "../../components/tables/PlatformsTable";
import { FROM_MYHUB } from "../../lib/fromMyHub";
import { MyHubSection } from "./MyHubSection";
import s from "./MyPlatforms.module.css";
import PlatformCard from "./MyPlatforms-PlatformCard";
import { readStarred, starredKey, writeStarred } from "./starredStorage";

type Row = { row: { original: PlatformListItem } };

type PlatformsView = "list" | "grid";

// "Completed" is reserved for platforms pushed to prod. Nothing tracks that
// yet, so no platform resolves to it.
const STATUS = {
  completed: { label: "Completed", icon: faCheck, className: "bfc-success" },
  inProgress: {
    label: "In progress",
    icon: faSpinner,
    className: "bfc-base-2",
  },
  notStarted: {
    label: "Not started",
    icon: faCircleDashed,
    className: "bfc-base-2",
  },
  failed: { label: "Failed", icon: faXmark, className: "bfc-alert" },
} as const;

function statusOf(platform: PlatformListItem) {
  if (platform.clientCount === 0) return STATUS.notStarted;
  if (platform.provisioningStatus === "failed") return STATUS.failed;
  return STATUS.inProgress;
}

// My Hub's own columns after the shared Name, Description and Clients: the
// role and status read as a personal summary.
const columnsAfterClients: Columns<PlatformListItem> = [
  {
    accessorKey: "userRole",
    Cell: ({ row }: Row) => <RoleBadge role={row.original.userRole} />,
    header: "Role",
    // gjallarbru's multi-select filter; its options are the roles present.
    filterVariant: "multi-select",
  },
  {
    id: "provisioningStatus",
    // The status as it reads in the cell, so the filter offers the same words.
    accessorFn: (row: PlatformListItem) => statusOf(row).label,
    Cell: ({ row }: Row) => {
      const status = statusOf(row.original);
      return (
        <span className={status.className} style={{ fontStyle: "italic" }}>
          <Icon icon={status.icon} style={{ marginRight: 6 }} />
          {status.label}
        </span>
      );
    },
    header: "Status",
    // gjallarbru's multi-select filter; its options are the statuses present.
    filterVariant: "multi-select",
  },
];

export default function MyPlatforms() {
  const { data: platforms } = usePlatforms();
  const { data: clients } = useClients();
  // How each section is shown: as a table, or as cards.
  const [allPlatformsView, setAllPlatformsView] =
    useState<PlatformsView>("list");
  const [favouritesView, setFavouritesView] = useState<PlatformsView>("grid");
  // Stars live in this browser's localStorage, one list per signed-in user.
  const storageKey = starredKey(auth.keycloak.tokenParsed?.sub);
  const [starred, setStarred] = useState(() => readStarred(storageKey));
  const myPlatforms = useMemo(
    () =>
      (platforms ?? []).filter(
        (p) => p.userRole === "owner" || p.userRole === "maintainer",
      ),
    [platforms],
  );

  const favourites = useMemo(
    () => myPlatforms.filter((p) => starred.has(p.id)),
    [myPlatforms, starred],
  );

  // Every client item carries its `platformId`, so one list serves every row.
  const clientsByPlatform = useMemo(() => {
    const byPlatform = new Map<string, ClientListItem[]>();
    for (const client of clients ?? []) {
      if (!client.platformId) continue;
      const existing = byPlatform.get(client.platformId);
      if (existing) existing.push(client);
      else byPlatform.set(client.platformId, [client]);
    }
    return byPlatform;
  }, [clients]);

  // Shared by the table's star column and the favourite cards.
  const toggle = useCallback(
    (id: string) => {
      const next = new Set(starred);
      if (!next.delete(id)) next.add(id);
      setStarred(next);
      writeStarred(storageKey, next);
    },
    [starred, storageKey],
  );

  // The favourite star sits before the shared Name, Description and Clients
  // columns.
  const columnsBefore = useMemo<Columns<PlatformListItem>>(
    () => [
      {
        // An accessor with a value keeps the table from rendering its empty
        // "–" placeholder in place of the Cell.
        accessorKey: "id",
        id: "favourite",
        Cell: ({ row }: Row) => {
          const { id, name } = row.original;
          const isStarred = starred.has(id);
          return (
            <Button
              state="neutral"
              variant="flat"
              noPadding
              aria-label={`Favourite ${name}`}
              aria-pressed={isStarred}
              onClick={() => toggle(id)}
            >
              <Icon
                className={isStarred ? "bfc-warning" : "bfc-base-2"}
                icon={isStarred ? faStarSolid : faStar}
              />
            </Button>
          );
        },
        enableColumnFilter: false,
        // Keeps the star out of the Columns menu and pinned first.
        enableColumnOrdering: false,
        enableHiding: false,
        enableSorting: false,
        // The name shows in the Columns menu; the header cell itself stays empty.
        header: "Favorites",
        Header: () => null,
        // Fixed width (24px padding + 18px icon + 24px padding), so it doesn't
        // stretch with the other columns.
        grow: false,
        size: 66,
      },
    ],
    [starred, toggle],
  );

  // The same cards serve Favorites and the card view of All platforms.
  const renderCards = (list: PlatformListItem[]) => (
    <div className={s.cardsArea}>
      <div className={s.cards}>
        {list.map((platform) => (
          <PlatformCard
            platform={platform}
            clients={clientsByPlatform.get(platform.id) ?? []}
            key={platform.id}
            onToggleStar={() => toggle(platform.id)}
            starred={starred.has(platform.id)}
          />
        ))}
      </div>
    </div>
  );

  // Favorites and All platforms are the same section: a header with a
  // list/grid switcher, then either the table or the cards.
  const renderPlatformsSection = ({
    platforms: list,
    empty,
    icon,
    onViewChange,
    tableName,
    title,
    view,
  }: {
    platforms: PlatformListItem[];
    // Shown instead of the table or cards when there are no platforms.
    empty?: ReactNode;
    icon: IconDefinition;
    onViewChange: (view: PlatformsView) => void;
    // Names the table's saved column layout, so each table keeps its own.
    tableName: string;
    title: string;
    view: PlatformsView;
  }) => (
    <MyHubSection
      title={title}
      icon={icon}
      view={view}
      onViewChange={onViewChange}
      empty={empty}
      isEmpty={list.length === 0}
      list={
        <PlatformsTable
          platforms={list}
          clientsByPlatform={clientsByPlatform}
          columnsAfter={columnsAfterClients}
          columnsBefore={columnsBefore}
          enablePagination={false}
          name={tableName}
          navigateState={FROM_MYHUB}
          rowClassName={s.row}
          toolbarRadius={false}
        />
      }
      cards={renderCards(list)}
    />
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2 className="bf-h2" style={{ margin: 0 }}>
          My platforms
        </h2>
        <Badge pill>
          <Icon icon={faCircleNodes} style={{ marginRight: 6 }} />
          {myPlatforms.length}
        </Badge>
      </div>
      <p
        className="bf-large bfc-base-2"
        style={{ marginTop: 8, marginBottom: 0 }}
      >
        Platforms you own and maintain
      </p>
      {renderPlatformsSection({
        platforms: favourites,
        empty: (
          <Message noIcon style={{ textAlign: "center" }}>
            No favorites selected yet. Click the star on a platform to make it a
            favorite.
          </Message>
        ),
        icon: faStar,
        onViewChange: setFavouritesView,
        tableName: "myhub-favourites",
        title: "Favorites",
        view: favouritesView,
      })}
      {renderPlatformsSection({
        platforms: myPlatforms,
        icon: faGrid,
        onViewChange: setAllPlatformsView,
        tableName: "myhub-platforms",
        title: "All platforms",
        view: allPlatformsView,
      })}
    </div>
  );
}
